import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { useTheme } from "hooks/useTheme";
import { CargaMasivaManager } from "managers";
import { CargaMasiva } from "models/ECF/CargaMasiva";
import EntidadSelector from "./components/EntidadSelector";
import PlantillaStep from "./components/PlantillaStep";
import DataPreviewGrid from "./components/DataPreviewGrid";
import ResultadoCarga from "./components/ResultadoCarga";

// Pasos del wizard
const PASO = {
  SELECCIONAR_ENTIDAD: 1,
  PLANTILLA_CARGA: 2,
  REVISAR_EDITAR: 3,
  RESULTADO: 4,
};

const PASOS_INFO = [
  { numero: PASO.SELECCIONAR_ENTIDAD, label: "Seleccionar Entidad" },
  { numero: PASO.PLANTILLA_CARGA, label: "Plantilla y Carga" },
  { numero: PASO.REVISAR_EDITAR, label: "Revisar y Editar" },
  { numero: PASO.RESULTADO, label: "Resultado" },
];

const CargaMasivaPage = ({ showLoading, hideLoading }) => {
  const [pasoActual, setPasoActual] = useState(PASO.SELECCIONAR_ENTIDAD);
  const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
  const [filas, setFilas] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [grabando, setGrabando] = useState(false);

  const { handleException } = useExceptionHandler();
  const { Message, Warning } = useMessage();
  const { theme } = useTheme();

  // -------------------------------------------------------------------------
  // Handlers del wizard
  // -------------------------------------------------------------------------
  const handleSeleccionarEntidad = (entidadValue) => {
    setEntidadSeleccionada(entidadValue);
    setFilas([]);
    setResultado(null);
    setPasoActual(PASO.PLANTILLA_CARGA);
  };

  const handleArchivoCargado = (filasParseadas) => {
    // Asegurar que cada fila tenga el Fila como número para tracking
    const filasConIndice = filasParseadas.map((f, idx) => ({
      ...f,
      Fila: f.Fila || idx + 2,
    }));
    setFilas(filasConIndice);
    setPasoActual(PASO.REVISAR_EDITAR);
  };

  const handleGuardar = (filasAGrabar) => {
    setGrabando(true);
    showLoading && showLoading();

    const body = CargaMasiva.buildGuardarRequest(entidadSeleccionada, filasAGrabar);

    CargaMasivaManager.guardar(body)
      .then((res) => {
        if (res.FilasConError > 0) {
          // Anotar filas con el error del backend y volver al paso 3
          const erroresMap = {};
          res.Resultados.forEach((r) => {
            if (!r.Exitoso) erroresMap[r.Fila] = r.Mensaje;
          });
          const filasAnotadas = filasAGrabar.map((f) => ({
            ...f,
            _errorBackend: erroresMap[f.Fila] || null,
          }));
          setFilas(filasAnotadas);
          setPasoActual(PASO.REVISAR_EDITAR);
          const detalleErrores = res.Resultados
            .filter((r) => !r.Exitoso)
            .map((r) => `Fila ${r.Fila}: ${r.Mensaje}`)
            .join(" | ");
          Warning({
            message: `${res.FilasConError} fila${res.FilasConError > 1 ? "s" : ""} con error. ${detalleErrores}`,
          });
        } else {
          setResultado(res);
          setPasoActual(PASO.RESULTADO);
          Message({ message: `${res.FilasExitosas} registros grabados exitosamente.` });
        }
      })
      .catch((e) => handleException(e))
      .finally(() => {
        setGrabando(false);
        hideLoading && hideLoading();
      });
  };

  const handleNuevaCarga = () => {
    setEntidadSeleccionada(null);
    setFilas([]);
    setResultado(null);
    setPasoActual(PASO.SELECCIONAR_ENTIDAD);
  };

  const handleVolverAtras = () => {
    if (pasoActual > PASO.SELECCIONAR_ENTIDAD) {
      setPasoActual(pasoActual - 1);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const entidadConfig = entidadSeleccionada
    ? CargaMasiva.getConfigEntidad(entidadSeleccionada)
    : null;

  return (
    <div
      style={{
        padding: "8px 16px 16px",
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: `calc(100vh - ${theme.MAX_HEIGHT_HEADER})`,
      }}
    >
      {/* Título */}
      <div className="mb-4">
        <h4 className="mb-0">Carga Masiva</h4>
        <small className="text-muted">
          Importa múltiples registros desde un archivo Excel
        </small>
      </div>

      {/* Stepper */}
      <div className="d-flex align-items-center mb-4">
        {PASOS_INFO.map((paso, idx) => (
          <React.Fragment key={paso.numero}>
            {idx > 0 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: pasoActual > paso.numero - 1 ? theme.color1 : "#dee2e6",
                  margin: "0 8px",
                }}
              />
            )}
            <div className="d-flex flex-column align-items-center" style={{ minWidth: 80 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  background:
                    pasoActual === paso.numero
                      ? theme.color1
                      : pasoActual > paso.numero
                      ? "#198754"
                      : "#dee2e6",
                  color: pasoActual >= paso.numero ? "#fff" : "#6c757d",
                }}
              >
                {pasoActual > paso.numero ? (
                  <i className="fas fa-check" style={{ fontSize: "0.75rem" }} />
                ) : (
                  paso.numero
                )}
              </div>
              <small
                className="mt-1 text-center"
                style={{
                  fontSize: "0.72rem",
                  color: pasoActual === paso.numero ? theme.color1 : "#6c757d",
                  fontWeight: pasoActual === paso.numero ? 600 : 400,
                }}
              >
                {paso.label}
              </small>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Card contenedora del paso actual */}
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <span style={{ fontWeight: 600 }}>
            {PASOS_INFO.find((p) => p.numero === pasoActual)?.label}
            {entidadConfig && pasoActual > PASO.SELECCIONAR_ENTIDAD && (
              <small className="text-muted ms-2" style={{ fontWeight: 400 }}>
                — {entidadConfig.label}
              </small>
            )}
          </span>
          {pasoActual > PASO.SELECCIONAR_ENTIDAD && pasoActual < PASO.RESULTADO && (
            <AnimatedIconButton
              onClick={handleVolverAtras}
              iconName="back"
              message="Volver"
              size="38px"
              height="22px"
              width="22px"
              disabled={grabando}
            />
          )}
        </Card.Header>
        <Card.Body>
          {pasoActual === PASO.SELECCIONAR_ENTIDAD && (
            <EntidadSelector onSeleccionar={handleSeleccionarEntidad} />
          )}

          {pasoActual === PASO.PLANTILLA_CARGA && entidadSeleccionada && (
            <PlantillaStep
              entidad={entidadSeleccionada}
              onArchivoCargado={handleArchivoCargado}
              showLoading={showLoading}
              hideLoading={hideLoading}
            />
          )}

          {pasoActual === PASO.REVISAR_EDITAR && entidadSeleccionada && (
            <DataPreviewGrid
              entidad={entidadSeleccionada}
              filas={filas}
              setFilas={setFilas}
              onGuardar={handleGuardar}
              disabled={grabando}
            />
          )}

          {pasoActual === PASO.RESULTADO && resultado && (
            <ResultadoCarga
              entidad={entidadSeleccionada}
              resultado={resultado}
              onNuevaCarga={handleNuevaCarga}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CargaMasivaPage;
